import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { UserContext } from "../userContext"
import Spinner, { ToggleSpinner } from "../components/spinner"
import { fetchResource } from "../../methods/fetchResource"
import { generateErrorMessage } from "../../methods/generateErrorMessage"
import { determineFilingYear } from "../../methods/determineFilingYear.mjs"

export default function Register() {
	const [message, setMessage] = useState("")
	const [ownerRecords, setOwnerRecords] = useState([])
	const [maritalStatuses, setMaritalStatuses] = useState([])
	const userContext = useContext(UserContext)
	const [isNewHX, setIsNewHX] = useState(false)
	const [maritalStatusCode, setMaritalStatusCode] = useState(null)
	const [prefileMessage, setPrefileMessage] = useState("")
	const [showSpouseNameField, setShowSpouseNameField] = useState(false)
	const CODE_MARRIED = 2
	const navigate = useNavigate()
	const [disableSubmitButton, setDisableSubmitButton] = useState()

	useEffect(() => {
		async function fetchData() {
			let ownerList = await fetchResource(`/api/ownerList?parcelID=${userContext.parcelID}`)

			if (!ownerList.success) {
				pageLoadDisabling(generateErrorMessage(ownerList.status))
				return
			}

			if (ownerList.result.rows.length < 1) {
				pageLoadDisabling(generateErrorMessage(60))
				return
			}

			setOwnerRecords(ownerList.result.rows)

			let maritalStatusList = await fetchResource("/api/maritalStatus/list")

			if (!maritalStatusList.success) {
				pageLoadDisabling(generateErrorMessage(maritalStatusList.status))
			}

			setMaritalStatuses(maritalStatusList.result.rows)
		}

		fetchData()
	}, [])

	function pageLoadDisabling(message) {
		setMessage(message)
		setDisableSubmitButton(true)
	}

	function validateApplicantRadios(event) {
		ownerRecords.forEach((owner, index) => {
			let applicantRadioButton = document.getElementById(`applicant|${index}`)

			if (event.target === applicantRadioButton) {
				return
			}

			if (applicantRadioButton.checked) {
				applicantRadioButton.checked = false
				document.getElementById(`neither|${index}`).checked = true
			}
		})

		checkToShowSpouseNameField()
	}

	function checkForEarlyPrefile(event) {
		let taxYear = determineFilingYear(event.target.value)
		let today = new Date()

		if (taxYear > today.getFullYear() && today.getMonth() < 3) {
			setPrefileMessage(`We are currently not processing exemption applications for ${taxYear}. You may continue to pre-file, but it will not be processed until all exemption applications for ${taxYear - 1} are processed.`)
			//document.getElementById("prefileNotice").scrollIntoView()
			return
		}

		setPrefileMessage("")
	}

	async function onSubmit(event) {
		event.preventDefault()

		if (userContext.parcelID === null) {
			setMessage("Please navigate to this page from your parcel.")
			//document.getElementById("registerMessage").scrollIntoView()
			return
		}

		let email = document.getElementById("inputEmail").value
		let verifyEmail = document.getElementById("inputVerifyEmail").value

		if (email !== verifyEmail) {
			setMessage("The applicant e-mail fields do not match.")
			//document.getElementById("registerMessage").scrollIntoView()
			return
		}

		setProcessingVisuals()

		userContext.email = email.toLowerCase()

		let applicants = []
		let allRecords = []
		let primaryExists = false
		let coApplicantCount = 0

		ownerRecords.forEach((owner, index) => {
			let applicantId = `applicant|${index}`
			let coApplicantId = `coApplicant|${index}`
			let neitherId = `neither|${index}`

			let applicantRadio = document.getElementById(applicantId)
			let coApplicantRadio = document.getElementById(coApplicantId)
			let neitherRadio = document.getElementById(neitherId)

			if (applicantRadio.checked) {
				applicants.push({ name: applicantRadio.name, is_primary: true, record_number: owner.record_number })
				primaryExists = true
				allRecords.push(index)
				return
			}

			if (coApplicantRadio && coApplicantRadio.checked) {
				applicants.push({ name: applicantRadio.name, is_primary: false, record_number: owner.record_number })
				allRecords.push(index)
				coApplicantCount++
				return
			}

			if (neitherRadio.checked) {
				allRecords.push(index)
				return
			}
		})

		if (applicants.length === 0 || !primaryExists) {
			setMessageAndHandleThings("You must select one of the above records to be an \"Applicant\" to continue.")
			return
		}

		if (applicants.length > 1 && maritalStatusCode !== CODE_MARRIED) {
			setMessageAndHandleThings("We currently do not support this ownership situation through this online portal. You will need to visit our office to continue with applying.")
			return
		}

		if (allRecords.length !== ownerRecords.length) {
			setMessageAndHandleThings("You must select one option per row above to continue.")
			return
		}

		if (coApplicantCount > 1) {
			setMessageAndHandleThings("You can only have one co-applicant selected. If your ownership situation requires more than one co-applicant, you will need to visit our office to apply.")
			return
		}

		if (!userContext.parcelHasHxCode && !userContext.bypassResidencyDueToApplication) {
			if (!document.getElementById("isHXYes").checked && !document.getElementById("isHXNo").checked) {
				setMessageAndHandleThings("Please select an option for the question above regarding if this property is your homestead.")
				return
			}

			if (document.getElementById("isHXYes").checked) {
				if (!document.getElementById("priorHomesteadYes").checked && !document.getElementById("priorHomesteadNo").checked) {
					setMessageAndHandleThings("Please select an option for the question above regarding if you previously received the benefit of a homestead exemption.")
					return
				}
			}

			if (coApplicantCount === 0 && maritalStatusCode === CODE_MARRIED) {
				if (document.getElementById("inputSpouseName").value.trim() === "") {
					setMessageAndHandleThings("Please select your spouse's name from the list above to be the \"Co-Applicant/Spouse\". If their name is not listed, please enter it in the field provided.")
					return
				}

				applicants.push({ name: document.getElementById("inputSpouseName").value.trim().toUpperCase(), is_primary: false, record_number: null })
			}
		}

		userContext.user_is_homestead = document.getElementById("isHXYes").checked
		let prior_homestead = document.getElementById("priorHomesteadYes").checked

		//default marital_status to 1/Single if we don't display any
		let marital_status = 1
		maritalStatuses.forEach((status) => {
			if (document.getElementById(`status|${status.code}`) && document.getElementById(`status|${status.code}`).checked) {
				marital_status = status.code
			}
		})

		let occupancy_date = document.getElementById("occupancyDate").value

		//Not allowed to enter an occpuancy date past today, must do this due to browsers providing a text field instead of date picker
		if (new Date(occupancy_date) > new Date()) {
			setMessageAndHandleThings("Your occupancy date cannot be a future date.")
			return
		}

		let register = await fetchResource("/api/register", {
			email,
			parcelID: userContext.parcelID,
			applicants,
			is_homestead: userContext.user_is_homestead,
			prior_homestead,
			marital_status,
			occupancy_date
		})

		if (register.success) {
			navigate("/login")
			return
		}

		setMessageAndHandleThings(generateErrorMessage(register.status))
	}

	function setProcessingVisuals() {
		setDisableSubmitButton(true)

		ToggleSpinner("spinner")
		//document.getElementById("spinner").scrollIntoView()

		setMessage("")
	}

	function setMessageAndHandleThings(message) {
		setDisableSubmitButton(false)

		ToggleSpinner("spinner")

		setMessage(message)
		//document.getElementById("registerMessage").scrollIntoView()
	}

	useEffect(() => {
		if (maritalStatusCode !== CODE_MARRIED) {
			setShowSpouseNameField(false)
			return
		}

		if (ownerRecords.length < 2) {
			setShowSpouseNameField(true)
			return
		}

		checkToShowSpouseNameField()
	}, [maritalStatusCode])

	function checkToShowSpouseNameField() {
		if (maritalStatusCode !== CODE_MARRIED) {
			return
		}

		let applicantSelected = false
		let coApplicantSelected = false
		let selectionCount = 0

		ownerRecords.forEach((owner, index) => {
			let applicantId = `applicant|${index}`
			let coApplicantId = `coApplicant|${index}`
			let neitherId = `neither|${index}`

			let applicantRadio = document.getElementById(applicantId)
			let coApplicantRadio = document.getElementById(coApplicantId)
			let neitherRadio = document.getElementById(neitherId)

			if (applicantRadio.checked) {
				applicantSelected = true
				selectionCount++
				return
			}

			if (coApplicantRadio && coApplicantRadio.checked) {
				coApplicantSelected = true
				selectionCount++
				return
			}

			if (neitherRadio.checked) {
				neitherRadio.checked
				selectionCount++
				return
			}
		})

		if (selectionCount === ownerRecords.length && applicantSelected && !coApplicantSelected) {
			setShowSpouseNameField(true)
			return
		}

		setShowSpouseNameField(false)
	}

	return (
		<div id="exemptions-portal--register" className="container">
			<div className="row">
				<div className="col-12">
					<h1 className="h1-style">Register</h1>
					<form className="bg-navy p-3 rounded text-light mb-3" onSubmit={onSubmit}>
						<div className="bg-navy-dark p-3 rounded mb-3">
							<h4 className="mb-3">
								Please enter the date you first started living at this property.
							</h4>
							<div className="input-group mb-1">
								<label htmlFor="occupancyDate" className="input-group-text">
									Date of Occupancy:
								</label>
								<input
									id="occupancyDate"
									type="date"
									required
									title="Please select your date of occupancy"
									max={new Date()}
									pattern="\d{4}-\d{2}-\d{2}"
									onChange={checkForEarlyPrefile}
									className="form-control"
									style={{maxWidth: "200px"}}
								/>
							</div>
							<h3 id="prefileNotice" className={`bg-warning text-dark p-3 rounded mt-3 mb-1 ${prefileMessage === "" ? "d-none" : ""}`}>
								{prefileMessage}
							</h3>
						</div>
						{
							userContext.parcelHasHxCode || userContext.bypassResidencyDueToApplication ?
							<input
								required
								hidden
								defaultChecked="true"
								type="radio"
								id="isHXYes"
								name="isHX"
							/> :
							<div className="bg-navy-dark p-3 rounded mb-3">
								<div className="mb-1">
									<h4 className="mb-3">
										Is this property your permanent residence?
									</h4>
									<div className="d-flex gap-3">
										<div className="">
											<input
												required
												type="radio"
												id="isHXYes"
												name="isHX"
												onChange={() => setIsNewHX(true)}
											/>
											<label htmlFor="isHXYes" className="ps-1">
												Yes
											</label>
										</div>
										<div>
											<input
												required
												type="radio"
												id="isHXNo"
												name="isHX"
												onChange={() => setIsNewHX(false)}
											/>
											<label htmlFor="isHXNo" className="ps-1">
												No
											</label>
										</div>
									</div>
								</div>
							</div>
						}
						{
							isNewHX ?
							<div className="bg-navy-dark p-3 rounded mb-3">
								<h4 className="mb-3">
									Has any applicant received the benefit of homestead exemption on another property in Florida in the last three years?
								</h4>
								<div className="d-flex gap-3">
									<div>
										<input
											required
											type="radio"
											id="priorHomesteadYes"
											name="priorHomestead"
										/>
										<label htmlFor="priorHomesteadYes" className="ps-1">
											Yes
										</label>
									</div>
									<div>
										<input
											required
											type="radio"
											id="priorHomesteadNo"
											name="priorHomestead"
										/>
										<label htmlFor="priorHomesteadNo" className="ps-1">
											No
										</label>
									</div>
								</div>
							</div> :
							<input
								hidden
								type="radio"
								id="priorHomesteadYes"
								name="priorHomestead"
							/>
						}
						{
							isNewHX &&
							<div className="bg-navy-dark p-3 rounded mb-3">
								<h4 className="mb-3">
									Please select your marital status.
								</h4>
								<div className="d-flex gap-3">
								{
									maritalStatuses.map((status) => {
										let statusID = `status|${status.code}`

										return (
											<div key={statusID}>
												<input
													required
													type="radio"
													id={statusID}
													name="maritalStatus"
													value={status.code}
													onClick={() => setMaritalStatusCode(status.code)}
												/>
												<label htmlFor={statusID} className="ps-1">
													{status.label}
												</label>
											</div>
										)
									})
								}
								</div>
							</div>
						}
						{
							// TODO - Currently unable to fully verify styling on this section
						}
						<div className="bg-navy-dark p-3 rounded mb-3">
							<h4 className="mb-3">
								Please mark each name below as one of the given options.
								<span className="d-block">
									Note: only one name can be marked as the "Applicant".
								</span>
							</h4>
							<div className="border rounded p-1" style={{maxWidth: "650px"}}>
								<table className="" style={{width: "100%"}}>
									<thead>
										<tr>
											<th className="text-center" style={{width: "80px"}}>
												Applicant
											</th>
											{
												isNewHX &&
												<th className="text-center" style={{width: "170px"}}>
													Co-Applicant/Spouse
												</th>
											}
											<th className="text-center" style={{width: "80px"}}>
												Neither
											</th>
											<th className="text-start">
												Name
											</th>
										</tr>
									</thead>
									<tbody>
										{
											ownerRecords.map((record, index) => {
												let splitOffName = record.name.split(" &")[0].trim()
												let partsOfName = splitOffName.split(" ")
												let lastName = partsOfName.shift()
												let rearrangedName = `${partsOfName.join(" ")} ${lastName}`

												let applicantId = `applicant|${index}`
												let coApplicantId = `coApplicant|${index}`
												let neitherId = `neither|${index}`
												let applicantTitle = `Mark ${rearrangedName} as Applicant`
												let coApplicantTitle = `Mark ${rearrangedName} as Co-Applicant/Spouse`
												let neitherTitle = `Mark ${rearrangedName} as Neither`

												return (
													<tr key={index}>
														<td className="text-center">
															<input
																required
																type="radio"
																id={applicantId}
																name={rearrangedName}
																value="Applicant"
																onChange={(event) => {
																	validateApplicantRadios(event);
																	checkToShowSpouseNameField
																}}
																title={applicantTitle}
															/>
														</td>
														{
															isNewHX &&
															<td className="text-center">
																<input
																	required
																	type="radio"
																	id={coApplicantId}
																	name={rearrangedName}
																	value="Co-Applicant"
																	title={coApplicantTitle}
																	onChange={checkToShowSpouseNameField}
																/>
															</td>
														}
														<td className="text-center">
															<input
																required
																type="radio"
																id={neitherId}
																name={rearrangedName}
																value="Neither"
																title={neitherTitle}
																onChange={checkToShowSpouseNameField}
															/>
														</td>
														<td>{rearrangedName}</td>
													</tr>
												)
											})
										}
									</tbody>
								</table>
							</div>
						</div>
						{
							showSpouseNameField &&
							<div className="bg-navy-dark p-3 rounded mb-3">
								<h4 className="mb-3">
									Please enter your spouse's name in the following field.
								</h4>
								<div className="input-group" style={{maxWidth: "650px"}}>
									<label htmlFor="inputSpouseName" className="input-group-text">
										Spouse's Full Name:
									</label>
									<input
										type="text"
										id="inputSpouseName"
										required
										title="Enter your spouse's name if their name is not available above."
										className="form-control"
										
									/>
								</div>
							</div>
						}
						<div className="bg-navy-dark p-3 rounded">
							<h4 className="mb-3">
								You will need to have access to the e-mail account you provide to log in.
							</h4>
							<div className="d-flex flex-column gap-2" style={{maxWidth: "650px"}}>
								<div className="input-group">
									<label
										htmlFor="inputEmail"
										className="input-group-text justify-content-end"
										style={{width: "190px"}}
									>
										Applicant E-mail:
									</label>
									<input
										type="email"
										id="inputEmail"
										required
										title="Enter the applicant's e-mail"
										className="form-control"
									/>
								</div>
								<div className="input-group">
									<label
										htmlFor="inputVerifyEmail"
										className="input-group-text justify-content-end"
										style={{width: "190px"}}
									>
										Verify Applicant E-mail:
									</label>
									<input
										type="email"
										id="inputVerifyEmail"
										required
										title="Re-enter the applicant's e-mail"
										className="form-control"
									/>
								</div>
							</div>
						</div>
						<button
							id="submitButton"
							type="submit"
							className={`btn btn-warning mt-3 ${disableSubmitButton ? "d-none" : ""}`}
							disabled={disableSubmitButton}
						>
							Register
						</button>
					</form>
					<Spinner id="spinner" />
					<h2 id="registerMessage" className={`bg-danger p-3 rounded ${message === "" ? "d-none" : ""}`}>
						{message}
					</h2>
				</div>
			</div>
		</div>
	)
}